import { useEffect, useState } from 'react';
import { client } from '../../api/claims';

/** complaints@nb.example.com from "Northbridge <Complaints@NB.example.com>". */
export const senderEmail = (raw: string | null | undefined): string => {
  const value = (raw || '').trim().toLowerCase();
  const bracket = value.match(/<([^<>\s]+@[^<>\s]+)>/);
  return bracket ? bracket[1] : value;
};

/** The corpus partition for a single-email example: caf_corpus_service._normalise_sender. */
export const senderPartition = (raw: string | null | undefined): string =>
  (raw || 'unknown').trim().toLowerCase() || 'unknown';

export interface LenderRule {
  kind: 'exact' | 'domain';
  match: string;
  label: string;
}

const DOMAIN = /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?\.[a-z]{2,63}$/;

/** Staff-configured lender addresses (Admin > Lender email filter), same rules as the dashboard. */
export const toLenderRules = (entries: Array<{ email_or_domain?: unknown; lender_name?: unknown }>): LenderRule[] =>
  entries.flatMap((entry) => {
    const raw = typeof entry.email_or_domain === 'string'
      ? entry.email_or_domain.trim().toLowerCase().replace(/^@/, '') : '';
    const label = typeof entry.lender_name === 'string' ? entry.lender_name.replace(/\s+/g, ' ').trim() : '';
    const parts = raw.split('@');
    const domain = parts[parts.length - 1] || '';
    if (!raw || !label || parts.length > 2 || !DOMAIN.test(domain)) return [];
    return [{ kind: raw.includes('@') ? 'exact' : 'domain', match: raw, label } as LenderRule];
  });

/** One lender name, or null when unmapped or ambiguous (never a guess). */
export const lenderFor = (sender: string | null | undefined, rules: LenderRule[]): string | null => {
  const email = senderEmail(sender);
  if (!email.includes('@')) return null;
  const labels = Array.from(new Set(
    rules
      .filter((rule) => (rule.kind === 'exact'
        ? email === rule.match
        : email.endsWith(`@${rule.match}`) || email.endsWith(`.${rule.match}`)))
      .map((rule) => rule.label),
  ));
  return labels.length === 1 ? labels[0] : null;
};

export const listLenderEmailRules = async (): Promise<LenderRule[]> => {
  const response = await client.get<{ entries?: Array<{ email_or_domain?: unknown; lender_name?: unknown }> }>(
    '/ui/admin/lender-emails',
  );
  const entries = response.data && Array.isArray(response.data.entries) ? response.data.entries : [];
  return toLenderRules(entries);
};

/** Loaded once per page view; on failure every sender simply shows without a lender name. `ready` is false until it settles. */
export const useLenderRulesReady = (): { rules: LenderRule[]; ready: boolean } => {
  const [state, setState] = useState<{ rules: LenderRule[]; ready: boolean }>({ rules: [], ready: false });
  useEffect(() => {
    let alive = true;
    listLenderEmailRules()
      .then((next) => { if (alive) setState({ rules: next, ready: true }); })
      .catch(() => { if (alive) setState({ rules: [], ready: true }); });
    return () => { alive = false; };
  }, []);
  return state;
};

export const useLenderRules = (): LenderRule[] => useLenderRulesReady().rules;
